import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Mail, Phone, MapPin } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { APP_VERSION } from "@/version";

export default function Footer() {
  const { site } = useSite();
  const f = site?.footer || {};
  const c = site?.contact_info || {};
  const s = site?.social_links || {};
  const logo = site?.site_settings?.logo_url || "/logo.png";

  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <img src={logo} alt="IAAJ logo" className="h-16 w-auto bg-white rounded-xl p-1.5 mb-4" />
            <p className="font-head font-bold text-white text-lg leading-tight">{f.org_name}</p>
            <p className="text-sm text-slate-400 mt-2 font-serif italic text-base">{f.tagline}</p>
          </div>

          <div>
            <h4 className="font-head font-semibold text-white mb-4">Explore</h4>
            <ul className="space-y-2 text-sm">
              {[["About Us", "/about"], ["Announcements", "/announcements"], ["Programs", "/programs"], ["Events", "/events"], ["Ministry in Action", "/gallery"]].map(([n, p]) => (
                <li key={p}><Link to={p} className="hover:text-brand-cyan transition-colors">{n}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-head font-semibold text-white mb-4">Get Involved</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/request-help" className="hover:text-brand-cyan transition-colors">Request Help</Link></li>
              <li><Link to="/request-prayer" className="hover:text-brand-cyan transition-colors">Request Prayer</Link></li>
              <li><Link to="/get-involved" className="hover:text-brand-cyan transition-colors">Volunteer / Donate</Link></li>
              <li><Link to="/contact" className="hover:text-brand-cyan transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-head font-semibold text-white mb-4">Reach Us</h4>
            <ul className="space-y-3 text-sm">
              {(c.address || c.city) && <li className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 text-brand-cyan shrink-0" /><span>{[c.address, c.city, c.state, c.zip].filter(Boolean).join(", ")}</span></li>}
              {c.phone && <li className="flex gap-2"><Phone className="w-4 h-4 text-brand-cyan shrink-0" /><a href={`tel:${c.phone}`} className="hover:text-brand-cyan">{c.phone}</a></li>}
              {c.email && <li className="flex gap-2"><Mail className="w-4 h-4 text-brand-cyan shrink-0" /><a href={`mailto:${c.email}`} className="hover:text-brand-cyan break-all">{c.email}</a></li>}
            </ul>
            {s.facebook && (
              <a href={s.facebook} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 bg-slate-800 hover:bg-teal px-4 py-2 rounded-full text-sm transition-colors" data-testid="footer-facebook">
                <Facebook className="w-4 h-4" /> Follow on Facebook
              </a>
            )}
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-6 flex flex-col items-center gap-3 text-sm text-slate-400 text-center">
          <p>{f.copyright}</p>
          <div className="flex gap-4">
            <Link to="/contact" className="hover:text-brand-cyan">Privacy</Link>
            <Link to="/admin" className="hover:text-brand-cyan" data-testid="footer-admin-link">Staff Login</Link>
          </div>
        </div>
        <div className="text-center mt-4 text-xs text-slate-500" data-testid="managed-by">
          {(f.managed_by_text || "Site Design and Managed by Prestigious Companies").replace(/\s*Prestigious Companies\s*$/i, " ")}
          <a href={f.managed_by_url || "https://www.prestigiouscompanies.org"} target="_blank" rel="noopener noreferrer" className="text-brand-cyan hover:underline font-medium" data-testid="prestigious-link">
            Prestigious Companies
          </a>
        </div>
        <div className="text-center mt-2 text-xs text-slate-600" data-testid="site-version">Version {APP_VERSION}</div>
      </div>
    </footer>
  );
}
